import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Search, Plus, User, Menu, X, Briefcase, ChevronRight, Star, MapPin } from "lucide-react";
import { http } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

export default function MarketplaceLayout({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [settings, setSettings] = useState({ marketplace_name: "CPM Marketplace" });
  const [categories, setCategories] = useState([]);
  const [searchQ, setSearchQ] = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    http.get("/marketplace/settings").then(r => setSettings(r.data)).catch(() => {});
    http.get("/marketplace/categories").then(r => setCategories(r.data)).catch(() => {});
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    if (searchQ.trim()) navigate(`/marketplace?q=${encodeURIComponent(searchQ.trim())}`);
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "#FDFBF7", fontFamily: "Manrope, sans-serif" }}>
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center gap-4">
          {/* Logo */}
          <Link to="/marketplace" className="flex items-center gap-2 shrink-0 mr-2">
            <div className="w-8 h-8 rounded-lg grid place-items-center" style={{ background: "#0F1E38" }}>
              <Briefcase size={16} className="text-yellow-400" />
            </div>
            <span className="font-bold text-base hidden sm:block" style={{ color: "#0F1E38", fontFamily: "Cormorant Garamond, serif" }}>
              {settings.marketplace_name || "CPM Marketplace"}
            </span>
          </Link>

          {/* Category nav — desktop */}
          <nav className="hidden lg:flex items-center gap-4 flex-1">
            <Link to="/marketplace" className={`text-sm font-medium transition-colors hover:text-yellow-600 ${location.pathname === "/marketplace" && !location.search ? "text-yellow-600" : "text-gray-600"}`}>All Services</Link>
            {categories.slice(0, 6).map(c => (
              <Link key={c.id} to={`/marketplace?category=${encodeURIComponent(c.slug)}`} className="text-sm font-medium text-gray-600 hover:text-yellow-600 transition-colors whitespace-nowrap">{c.name}</Link>
            ))}
          </nav>

          {/* Search */}
          <form onSubmit={onSearch} className="flex-1 max-w-sm hidden md:flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-full px-4 py-2">
            <Search size={15} className="text-gray-400 shrink-0" />
            <input value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="Search services, businesses…" className="flex-1 text-sm bg-transparent outline-none text-gray-700 placeholder-gray-400" />
          </form>

          {/* Actions */}
          <div className="flex items-center gap-3 ml-auto md:ml-0">
            {user ? (
              <>
                <Link to="/marketplace/create" className="hidden md:flex items-center gap-1.5 px-4 py-2 rounded-full font-bold text-sm text-white" style={{ background: "#C5A028" }}>
                  <Plus size={15} /> List a Service
                </Link>
                <Link to="/marketplace/my-listings" className="hidden md:flex items-center gap-1.5 text-sm font-semibold text-gray-700 hover:text-yellow-600 transition-colors"><User size={16} /> My Listings</Link>
              </>
            ) : (
              <Link to="/auth" className="hidden md:flex items-center gap-1.5 px-4 py-2 rounded-full font-bold text-sm text-white" style={{ background: "#0F1E38" }}>Sign in to list</Link>
            )}
            <button onClick={() => setMobileMenuOpen(o => !o)} className="md:hidden p-2 rounded-full hover:bg-gray-100 transition-colors">
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* Category ribbon — desktop */}
        {categories.length > 0 && (
          <div className="hidden md:block border-t border-gray-100">
            <div className="max-w-7xl mx-auto px-4 h-10 flex items-center gap-4 overflow-x-auto scrollbar-none">
              {categories.map(c => (
                <Link key={c.id} to={`/marketplace?category=${encodeURIComponent(c.slug)}`} className="shrink-0 text-xs font-semibold text-gray-500 hover:text-yellow-600 transition-colors whitespace-nowrap">{c.icon && <span className="mr-1">{c.icon}</span>}{c.name}</Link>
              ))}
            </div>
          </div>
        )}
      </header>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-30 bg-white pt-16 overflow-y-auto" onClick={() => setMobileMenuOpen(false)}>
          <div className="p-4 space-y-2">
            <form onSubmit={(e) => { onSearch(e); setMobileMenuOpen(false); }} className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 mb-4">
              <Search size={16} className="text-gray-400" />
              <input value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="Search services…" className="flex-1 text-sm bg-transparent outline-none" />
            </form>
            {user && (
              <Link to="/marketplace/create" className="flex items-center gap-3 px-4 py-3 rounded-xl font-bold text-yellow-700 bg-yellow-50 border border-yellow-100">
                <Plus size={18} /> List a Service
              </Link>
            )}
            {[
              { to: "/marketplace", label: "All Services" },
              { to: "/marketplace/my-listings", label: "My Listings" },
              { to: "/app", label: "← CPM Community" },
            ].map(({ to, label }) => (
              <Link key={to} to={to} className="flex items-center justify-between px-4 py-3 rounded-xl font-semibold text-gray-700 hover:bg-gray-50">
                {label}<ChevronRight size={14} className="text-gray-400" />
              </Link>
            ))}
            <div className="pt-2 border-t border-gray-100">
              <div className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-gray-400">Categories</div>
              {categories.map(c => (
                <Link key={c.id} to={`/marketplace?category=${encodeURIComponent(c.slug)}`} className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-50 rounded-xl">
                  {c.icon && <span>{c.icon}</span>}{c.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Page content */}
      <main className="flex-1 max-w-7xl mx-auto w-full px-4 py-6 md:py-10">
        {children}
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-100 bg-white mt-auto">
        <div className="max-w-7xl mx-auto px-4 py-8 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-gray-500">
          <div className="flex items-center gap-2"><Briefcase size={16} className="text-yellow-600" /><span className="font-semibold" style={{ color: "#0F1E38" }}>{settings.marketplace_name}</span></div>
          <div className="flex items-center gap-6">
            <Link to="/marketplace" className="hover:text-yellow-600">Browse Services</Link>
            <Link to="/marketplace/create" className="hover:text-yellow-600">List a Service</Link>
            <Link to="/store" className="hover:text-yellow-600">CPM Store</Link>
            <Link to="/app" className="hover:text-yellow-600">← Community</Link>
          </div>
          <div>© {new Date().getFullYear()} CelestialPeopleMeet</div>
        </div>
      </footer>
    </div>
  );
}
